import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Layout from "components/Layout";
import api from "utils/api";
import { setUser } from "state";
import { formatPrice } from "constants/massages";
import "styles/topup.css";

const BONUS_OPTIONS = [
  { amountCents: 5000, creditCents: 5500 },
  { amountCents: 10000, creditCents: 11500 },
];

const parseEuroToCents = (value) => {
  const normalized = String(value || "")
    .trim()
    .replace(/\s/g, "")
    .replace(",", ".");
  if (!/^\d+(\.\d{0,2})?$/.test(normalized)) return null;
  const amount = Number(normalized);
  if (!Number.isFinite(amount)) return null;
  return Math.round(amount * 100);
};

const creditForAmount = (amountCents) => {
  if (amountCents >= 10000) return amountCents + Math.round(amountCents * 0.15);
  if (amountCents >= 5000) return amountCents + Math.round(amountCents * 0.1);
  return amountCents;
};

const TopUpPage = () => {
  const user = useSelector((s) => s.user);
  const dispatch = useDispatch();
  const location = useLocation();
  const [amount, setAmount] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState(null);

  const amountCents = useMemo(() => parseEuroToCents(amount), [amount]);
  const creditCents =
    amountCents && amountCents > 0 ? creditForAmount(amountCents) : null;
  const bonusCents =
    creditCents && amountCents ? Math.max(0, creditCents - amountCents) : 0;
  const currentBalanceCents = Math.max(0, Number(user?.referralBalanceCents || 0));

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) {
      if (params.get("cancelled")) {
        setStatus({ kind: "info", text: "Die Aufladung wurde abgebrochen." });
      }
      return undefined;
    }

    let cancelled = false;
    api
      .post("/payment/balance-topup/confirm", { sessionId })
      .then(({ data }) => {
        if (cancelled) return;
        if (data?.user) dispatch(setUser({ user: data.user }));
        setStatus({
          kind: data?.confirmed ? "success" : "info",
          text: data?.confirmed
            ? "Dein Kontoguthaben wurde aktualisiert."
            : "Die Zahlung wird noch verarbeitet. Dein Guthaben aktualisiert sich gleich.",
        });
      })
      .catch((err) => {
        if (cancelled) return;
        setStatus({
          kind: "error",
          text:
            err?.response?.data?.message ||
            "Die Aufladung konnte noch nicht bestätigt werden.",
        });
      });

    return () => {
      cancelled = true;
    };
  }, [dispatch, location.search]);

  const chooseAmount = (cents) => {
    setAmount(String(cents / 100));
    setStatus(null);
  };

  const submitTopUp = async (event) => {
    event.preventDefault();
    setStatus(null);

    if (!amountCents || amountCents < 100) {
      setStatus({
        kind: "error",
        text: "Bitte gib mindestens 1 € ein.",
      });
      return;
    }

    setSubmitting(true);
    try {
      const { data } = await api.post("/payment/balance-topup/checkout", {
        amountCents,
      });
      if (!data?.url) throw new Error("Stripe-Link fehlt.");
      window.location.href = data.url;
    } catch (err) {
      setStatus({
        kind: "error",
        text:
          err?.response?.data?.message ||
          err?.message ||
          "Die Zahlungsseite konnte nicht geöffnet werden.",
      });
      setSubmitting(false);
    }
  };

  return (
    <Layout>
      <section className="topup-page">
        <div className="topup-shell">
          <header className="topup-header">
            <div>
              <h1>
                Guthaben <em>aufladen</em>
              </h1>
              <p>
                Lade hier dein Kontoguthaben auf und nutze es direkt bei
                deiner nächsten Dr.-Hoffmann-Buchung.
              </p>
            </div>
            <div className="topup-balance">
              <span>Aktuelles Guthaben</span>
              <strong>{formatPrice(currentBalanceCents)}</strong>
            </div>
          </header>

          <p className="topup-referral-note">
            Du möchtest dir Massage-Guthaben dazuverdienen?{" "}
            <Link to="/empfehlungsprogramm">Empfiehl</Link> uns weiter und
            sichere dir 5 € Guthaben.
          </p>

          <form className="topup-panel" onSubmit={submitTopUp}>
            <label htmlFor="topup-amount">Betrag</label>
            <div className="topup-input-row">
              <input
                id="topup-amount"
                type="text"
                inputMode="decimal"
                autoComplete="off"
                placeholder="z. B. 25"
                value={amount}
                onChange={(event) => setAmount(event.target.value)}
              />
              <span>€</span>
            </div>

            {creditCents && (
              <p className="topup-preview">
                Du zahlst {formatPrice(amountCents)} und bekommst{" "}
                <strong>{formatPrice(creditCents)}</strong> gutgeschrieben.
                {bonusCents > 0 && (
                  <span> Bonus: {formatPrice(bonusCents)}</span>
                )}
              </p>
            )}

            <div className="topup-bonus-grid" aria-label="Bonusoptionen">
              {BONUS_OPTIONS.map((option) => (
                <button
                  type="button"
                  key={option.amountCents}
                  className={`topup-bonus-option ${
                    amountCents === option.amountCents ? "is-selected" : ""
                  }`}
                  onClick={() => chooseAmount(option.amountCents)}
                >
                  <span>{formatPrice(option.amountCents)}</span>
                  <strong>
                    Du bekommst {formatPrice(option.creditCents)} gutgeschrieben
                  </strong>
                </button>
              ))}
            </div>

            {status && (
              <div className={`auth-alert is-${status.kind}`}>{status.text}</div>
            )}

            <div className="topup-actions">
              <Link to="/buchen" className="btn-ghost">
                Zur Buchung
              </Link>
              <button type="submit" className="btn-primary" disabled={submitting}>
                {submitting ? "Weiterleitung..." : "Guthaben aufladen"}
              </button>
            </div>
          </form>
        </div>
      </section>
    </Layout>
  );
};

export default TopUpPage;
